<!--  
    Functions as a percentage bar that stacks each category provided based on its percentage.
    
    - Example categories prop: 
    
        [
            { name: "completed", cssClass: "completed", quantity: 5 },
            { name: "incomplete", cssClass: "incompleted", quantity: 3 }
        ]
-->
<template>
  <div class="stacked-column-bar" :style="cssVars">
    <!-- Display value & label on top of bar -->
    <div v-if="showHeader" class="header">
      <div
        v-for="category in categories"
        :key="getKey(categoryName(category))"
        class="title"
        :class="categoryCssClass(category)"
        @click="emitStatusFilter(category)"
      >
        <div class="value">{{ categoryValue(category) }}</div>
        <div class="label">{{ categoryName(category) }}</div>
      </div>
    </div>

    <!-- Bar -->
    <div class="bar-wrapper">
      <div
        v-for="category in barCategory"
        :key="getKey(categoryName(category))"
        class="bar"
        :class="categoryCssClass(category)"
        :style="styleObject(category)"
        @click="emitStatusFilter(category)"
      >
        <div v-if="showValueCount" class="value">{{ categoryValue(category) }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from "@utils/var.check";
import subjectColorMap from "@configs/subjectColorMap";
import { SCALE_OPTIONS } from "@modules/content-mastery/service/content.mastery.constant";
import { computed } from "vue";
import { StackedColumnBarCategory } from "@plugins/common/types.ts";

// Props
interface Props {
  categories?: StackedColumnBarCategory[];
  maxValue?: number;
  showHeader?: boolean;
  showValueCount?: boolean;
}

const {
  categories = null,
  maxValue = 1,
  showHeader = true,
  showValueCount = true,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  changeStatusFilter: [data: unknown];
}>();

// Computed
const barCategory = computed(() => {
  //to not consider category with 0 quantity
  return categories.filter((category) => {
    return category.quantity > 0;
  });
});

const categoryName = computed(() => {
  return (category) => {
    return !isEmpty(category) ? category.name : "";
  };
});

const categoryValue = computed(() => {
  return (category) => {
    return !isEmpty(category) ? category.quantity : 0;
  };
});

const categoryCssClass = computed(() => {
  return (category) => {
    return !isEmpty(category) ? category.cssClass : "";
  };
});

const styleObject = computed(() => {
  // calculates the width that a category should occupy in the bar.
  return (category) => {
    return {
      width: `${(category.quantity / maxValue) * 100}%`,
    };
  };
});

const assignmentColor = computed(() => {
  const assignmentCategory = categories.find((category) => {
    return category.name === SCALE_OPTIONS.ASSIGNMENT_SCORE;
  });
  const subjectKey = assignmentCategory ? assignmentCategory.subjectKey : "";
  return subjectColorMap.getSubjectColor(subjectKey);
});

const cssVars = computed(() => {
  return {
    "--assignmentColor": assignmentColor.value,
  };
});

// Methods
const getKey = (categoryName) => {
  return `${Date.now()}_${categoryName}`;
};

const emitStatusFilter = (category) => {
  const status = !isEmpty(category.value) ? category.value : category.name;
  emit("changeStatusFilter", status);
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.stacked-column-bar {
  display: flex;
  flex-flow: column wrap;
  flex: 1;

  > div {
    display: flex;
    flex: 1;
  }

  .value,
  .label {
    cursor: pointer;
  }

  .title {
    flex: 1 1 0%;
    text-align: center;
    padding: 0 $spacing-05;
    margin: $spacing-03 0;

    .value {
      // TODO [FE]: No corresponding type token found for this. To feedback to designer for follow-up
      font-size: 2rem;
      font-weight: 700;
      line-height: 1;
    }

    .label {
      @include carbon--type-style("helper-text-01", true);
      text-transform: uppercase;
      color: $text-02;
    }

    &.completed {
      .value {
        color: $support-04;
      }
    }

    &.in-progress {
      .value {
        color: $support-03;
      }
    }

    &.pending-mark {
      .value {
        color: $state-partial;
      }
    }

    &.incomplete {
      .value {
        color: $support-03;
      }
    }

    &.overdue {
      .value {
        color: $support-01;
      }
    }

    + .title {
      border-left: 1px solid $ui-04;
    }
  }

  .bar {
    @include carbon--type-style("helper-text-01", true);
    min-width: 1rem;
    min-height: 10px;

    &:first-child {
      border-radius: 5px 0 0 5px;
    }

    &:last-child {
      border-radius: 0 5px 5px 0;
    }

    &:first-child:last-child {
      border-radius: 5px;
    }

    &.completed {
      background-color: $support-04;
      color: $text-04;
    }

    &.in-progress,
    &.incomplete {
      background-color: $state-info;
      color: $state-info-text;
    }

    &.pending-mark {
      background-color: $state-partial;
      color: $text-04;
    }

    &.overdue {
      background-color: $support-01;
      color: $text-04;
    }

    &.sdl-score {
      background-color: $inverse-link;
    }

    &.assignment-score {
      background-color: var(--assignmentColor);
    }

    &.mark {
      background-color: $ui-03;
    }

    &[class*="als"] {
      background-color: $ai;
    }

    &.als-mastery {
      background-color: $mastered;
    }

    &.mastery {
      background-color: $inverse-support-04;
    }
  }

  .value {
    text-align: center;
  }
}
</style>
